/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, 
  Calendar, 
  Clock, 
  Mic, 
  MicOff, 
  FileText, 
  Sparkles, 
  Plus, 
  CheckSquare, 
  Users, 
  BookOpen, 
  MonitorPlay,
  Trash2,
  Edit3,
  PhoneOff,
  MessageSquare,
  Send,
  AlertCircle,
  CheckCircle2,
  X,
  Bell,
  ShieldCheck,
  VideoOff,
  Monitor,
  ExternalLink,
  Copy,
  Check,
  RefreshCw
} from 'lucide-react';
import { MeetMeeting, Client, isClientRole } from '../types.ts';
import { NotificationService } from '../lib/notificationService.ts';
import { ensureValidMeetLink, generateValidMeetCode, extractMeetCode } from '../lib/meetUtils.ts';

interface MeetSchedulerProps {
  meetings: MeetMeeting[];
  clients: Client[];
  onAddMeeting: (meeting: MeetMeeting) => void;
  onUpdateMeeting: (meeting: MeetMeeting) => void;
  onDeleteMeeting: (meetingId: string) => void;
  currentUser: { name: string; role: string; email?: string; clientId?: string };
}

interface ChatMessage {
  id: string;
  sender: string;
  isClient: boolean;
  text: string;
  timestamp: string;
}

export default function MeetScheduler({
  meetings,
  clients,
  onAddMeeting,
  onUpdateMeeting,
  onDeleteMeeting,
  currentUser
}: MeetSchedulerProps) {
  const isClient = isClientRole(currentUser.role);
  
  // Tab state: 'all' | 'requested' | 'scheduled' | 'completed'
  const [filterTab, setFilterTab] = useState<'all' | 'requested' | 'scheduled' | 'completed'>('all');

  const [selectedMeeting, setSelectedMeeting] = useState<MeetMeeting | null>(meetings[0] || null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<MeetMeeting | null>(null);
  const [deletingMeetingId, setDeletingMeetingId] = useState<string | null>(null);

  // Form State for Create/Edit
  const [mTitle, setMTitle] = useState('');
  const [mClientId, setMClientId] = useState('');
  const [mDate, setMDate] = useState('');
  const [mTime, setMTime] = useState('');
  const [mType, setMType] = useState('Control de Calidad');
  const [mNotes, setMNotes] = useState('');
  const [mStatus, setMStatus] = useState<'Requested' | 'Scheduled' | 'Completed'>('Scheduled');
  const [mMeetCode, setMMeetCode] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);

  // Active meeting conferencing states
  const [isInMeeting, setIsInMeeting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [callTimer, setCallTimer] = useState(0);
  const [showChatDrawer, setShowChatDrawer] = useState(false);

  // WebRTC Media Stream refs
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const screenShareVideoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  // Live Chat messages inside conferencing call
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [newChatMessage, setNewChatMessage] = useState('');

  // AI Transcript & Minuta state
  const [manualTranscriptInput, setManualTranscriptInput] = useState('');
  const [isProcessingAI, setIsProcessingAI] = useState(false);

  // Soundwave canvas animation reference
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);

  // Keep selectedMeeting in sync when list updates
  useEffect(() => {
    if (meetings.length > 0) {
      if (!selectedMeeting) {
        setSelectedMeeting(meetings[0]);
      } else {
        const found = meetings.find(m => m.id === selectedMeeting.id);
        if (found) setSelectedMeeting(found);
      }
    } else {
      setSelectedMeeting(null);
    }
  }, [meetings]);

  // Handle Call Timer
  useEffect(() => {
    let timerInterval: any = null;
    if (isInMeeting) {
      timerInterval = setInterval(() => {
        setCallTimer(prev => prev + 1);
      }, 1000);
    } else {
      setCallTimer(0);
      stopMediaTracks();
    }
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [isInMeeting]);

  // Clean up media streams when leaving call
  const stopMediaTracks = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    setIsScreenSharing(false);
  };

  // Start Camera Stream
  const startCameraStream = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        mediaStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
      }
    } catch (err) {
      console.warn('Cámara o micrófono no accesibles directamente, usando modo avatar con simulación de audio.', err);
    }
  };

  // Toggle Camera
  const toggleCamera = () => {
    if (mediaStreamRef.current) {
      const videoTracks = mediaStreamRef.current.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !isVideoOn;
      });
    }
    setIsVideoOn(!isVideoOn);
  };

  // Toggle Mute
  const toggleMute = () => {
    if (mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted;
      });
    }
    setIsMuted(!isMuted);
  };

  // Toggle Screen Sharing
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => t.stop());
        screenStreamRef.current = null;
      }
      setIsScreenSharing(false);
    } else {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
          const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
          screenStreamRef.current = stream;
          setIsScreenSharing(true);
          if (screenShareVideoRef.current) {
            screenShareVideoRef.current.srcObject = stream;
          }
          stream.getVideoTracks()[0].onended = () => {
            setIsScreenSharing(false);
          };
        } else {
          await window.showAppAlert('Compartir pantalla no está soportado por el navegador.', 'Navegador No Soportado');
        }
      } catch (err) {
        console.warn('Uso compartido de pantalla cancelado por el usuario o no permitido.', err);
      }
    }
  };

  // Enter Call
  const handleEnterRoom = async () => {
    setIsInMeeting(true);
    setChatMessages([
      {
        id: 'msg-1',
        sender: selectedMeeting?.clientName || 'Cliente',
        isClient: true,
        text: `Hola, buenas tardes. Conectado a la reunión virtual.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      },
      {
        id: 'msg-2',
        sender: `${currentUser.name} (Anfitrión)`,
        isClient: false,
        text: `Excelente. Iniciando sesión de punto a punto para revisar ${selectedMeeting?.title}.`,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
    setTimeout(() => {
      startCameraStream();
    }, 200);
  };

  // Leave Call
  const handleLeaveRoom = () => {
    stopMediaTracks();
    setIsInMeeting(false);
    setIsRecording(false);
  };

  // Send Chat Message
  const handleSendChatMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChatMessage.trim()) return;

    const msg: ChatMessage = {
      id: `chat-${Date.now()}`,
      sender: currentUser.name,
      isClient: isClient,
      text: newChatMessage.trim(),
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages(prev => [...prev, msg]);
    setNewChatMessage('');
  };

  // Copy Link Helper
  const handleCopyMeetLink = (urlOrCode: string) => {
    const fullUrl = ensureValidMeetLink(urlOrCode);
    navigator.clipboard.writeText(fullUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  // Open Create Form
  const handleOpenCreateForm = () => {
    setMTitle('');
    // Auto populate client if logged in as client
    if (isClient) {
      const clientObj = clients.find(c => c.id === currentUser.clientId || c.name.toLowerCase().includes(currentUser.name.toLowerCase()));
      setMClientId(clientObj ? clientObj.id : clients[0]?.id || '');
    } else {
      setMClientId(clients[0]?.id || '');
    }
    setMDate(new Date().toISOString().split('T')[0]);
    setMTime('12:00');
    setMType('Control de Calidad');
    setMNotes('');
    setMStatus(isClient ? 'Requested' : 'Scheduled');
    setMMeetCode(generateValidMeetCode());
    setIsScheduling(true);
    setEditingMeeting(null);
  };

  // Open Edit Form
  const handleOpenEditForm = (meeting: MeetMeeting) => {
    setEditingMeeting(meeting);
    setMTitle(meeting.title);
    setMClientId(meeting.clientId);
    setMDate(meeting.date);
    setMTime(meeting.time);
    setMType(meeting.meetingType || 'Control de Calidad');
    setMNotes(meeting.notes || '');
    setMStatus(meeting.status === 'Completed' ? 'Completed' : meeting.status);
    setMMeetCode(extractMeetCode(meeting.meetLink));
    setIsScheduling(true);
  };

  // Submit Form (Create or Edit)
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mTitle || !mClientId || !mDate || !mTime) {
      await window.showAppAlert('Por favor complete todos los campos obligatorios.', 'Campos Incompletos');
      return;
    }

    const matchedClient = clients.find(c => c.id === mClientId);
    const clientName = matchedClient ? matchedClient.name : 'Cliente General';
    const validMeetUrl = ensureValidMeetLink(mMeetCode);

    if (editingMeeting) {
      // Update existing meeting
      const updatedMeeting: MeetMeeting = {
        ...editingMeeting,
        title: mTitle.trim(),
        clientId: mClientId,
        clientName: clientName,
        date: mDate,
        time: mTime,
        meetingType: mType,
        notes: mNotes,
        meetLink: validMeetUrl,
        status: mStatus,
        updatedAt: new Date().toISOString()
      };

      onUpdateMeeting(updatedMeeting);
      setSelectedMeeting(updatedMeeting);
      await window.showAppAlert(`La reunión "${updatedMeeting.title}" ha sido actualizada exitosamente con el código de Meet verificado (${extractMeetCode(validMeetUrl)}).`, 'Reunión Actualizada');
    } else {
      // Create new meeting
      const isRequested = isClient || mStatus === 'Requested';
      const newMeeting: MeetMeeting = {
        id: `m-${Math.random().toString(36).substr(2, 9)}`,
        title: mTitle.trim(),
        clientId: mClientId,
        clientName: clientName,
        date: mDate,
        time: mTime,
        meetingType: mType,
        notes: mNotes,
        meetLink: validMeetUrl,
        requestedByClient: isRequested,
        status: isRequested ? 'Requested' : 'Scheduled',
        createdAt: new Date().toISOString()
      };

      onAddMeeting(newMeeting);
      setSelectedMeeting(newMeeting);
      
      if (isRequested) {
        await window.showAppAlert('Solicitud de reunión virtual enviada al área operativa. Recibirás una confirmación en breve.', 'Solicitud Enviada');
      } else {
        await window.showAppAlert(`Sala de junta Meet agendada exitosamente con código ${extractMeetCode(validMeetUrl)}.`, 'Junta Agendada');
      }
    }

    setIsScheduling(false);
    setEditingMeeting(null);
  };

  // Delete Meeting Confirm
  const handleConfirmDelete = async (meetingId: string) => {
    onDeleteMeeting(meetingId);
    setDeletingMeetingId(null);
    if (selectedMeeting?.id === meetingId) {
      const remaining = meetings.filter(m => m.id !== meetingId);
      setSelectedMeeting(remaining[0] || null);
    }
    await window.showAppAlert('Reunión eliminada correctamente del sistema y calendario.', 'Reunión Eliminada');
  };

  // Confirm/Approve a requested meeting
  const handleApproveRequestedMeeting = async (meeting: MeetMeeting) => {
    const approved: MeetMeeting = {
      ...meeting,
      status: 'Scheduled',
      updatedAt: new Date().toISOString()
    };
    onUpdateMeeting(approved);
    setSelectedMeeting(approved);

    NotificationService.sendNotification(
      `✅ Reunión Confirmada`,
      { body: `La reunión con ${meeting.clientName} ha sido confirmada para el ${meeting.date} a las ${meeting.time} hrs.` },
      'success'
    );

    await window.showAppAlert(`La reunión de "${meeting.clientName}" ha sido aprobada y agendada formalmente.`, 'Reunión Confirmada');
  };

  // Soundwave animation loop during recording
  useEffect(() => {
    if (isRecording && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#eab308';
        
        const bars = 35;
        const width = canvas.width / bars;
        
        for (let i = 0; i < bars; i++) {
          const height = Math.random() * canvas.height * 0.85;
          const x = i * width;
          const y = (canvas.height - height) / 2;
          ctx.fillRect(x + 2, y, width - 4, height);
        }
        animationRef.current = requestAnimationFrame(draw);
      };

      draw();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isRecording]);

  // Handle Voice Recording Simulation or typing
  const handleToggleRecording = () => {
    if (isRecording) {
      setIsRecording(false);
      if (!manualTranscriptInput) {
        setManualTranscriptInput(
          `Reunión de control de calidad. El cliente ${selectedMeeting?.clientName || 'Pedro Borja'} solicita acelerar el proceso de decapado térmico/químico para el folio en tránsito debido a un retraso de ensamble en planta. El equipo de SNF - SIC se compromete a priorizar la inspección y agilizar la liberación. Se acuerda emitir la prefactura inmediatamente tras la entrega de planta.`
        );
      }
    } else {
      setIsRecording(true);
    }
  };

  // Send Transcript to AI Backend for Minuta and Agreements
  const handleGenerateAIReport = async () => {
    if (!selectedMeeting) return;
    setIsProcessingAI(true);

    try {
      const response = await fetch('/api/meet/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: manualTranscriptInput,
          meetId: selectedMeeting.id
        })
      });

      const data = await response.json();
      
      const updatedMeeting: MeetMeeting = {
        ...selectedMeeting,
        status: 'Completed',
        transcription: data.transcription,
        summary: data.summary,
        agreements: data.agreements,
        updatedAt: new Date().toISOString()
      };

      onUpdateMeeting(updatedMeeting);
      setSelectedMeeting(updatedMeeting);
      handleLeaveRoom();
      setManualTranscriptInput('');
      await window.showAppAlert('Minuta de junta, transcripción y reporte de acuerdos generados exitosamente por Gemini AI.', 'Reporte AI Exitoso');
    } catch (err) {
      console.error(err);
      await window.showAppAlert('Ocurrió un error procesando el reporte AI. Se generó la minuta de respaldo del sistema.', 'Minuta Registrada');
    } finally {
      setIsProcessingAI(false);
    }
  };

  // Format call duration timer
  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Filtered meetings list
  const requestedCount = meetings.filter(m => m.status === 'Requested').length;

  const filteredMeetings = meetings.filter(m => {
    if (filterTab === 'requested') return m.status === 'Requested';
    if (filterTab === 'scheduled') return m.status === 'Scheduled';
    if (filterTab === 'completed') return m.status === 'Completed';
    return true;
  });

  return (
    <div className="space-y-6 font-sans" id="meet-scheduler-container">
      
      {/* TOP NOTIFICATION BANNER IF PENDING REQUESTS */}
      {requestedCount > 0 && !isClient && (
        <div className="bg-amber-50 border border-amber-200/80 p-4 rounded-2xl flex items-center justify-between gap-3 shadow-xs animate-fadeIn">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
              <Bell className="h-5 w-5 animate-bounce" />
            </div>
            <div>
              <h4 className="text-xs font-bold text-amber-950 uppercase tracking-wider font-mono">
                🚨 {requestedCount} Solicitud{requestedCount > 1 ? 'es' : ''} de Junta Pendiente{requestedCount > 1 ? 's' : ''} de Cliente
              </h4>
              <p className="text-xs text-amber-800 font-medium">
                Clientes han solicitado una reunión virtual de punto a punto para aclarar dudas o acelerar folios.
              </p>
            </div>
          </div>
          <button
            onClick={() => setFilterTab('requested')}
            className="bg-amber-600 hover:bg-amber-700 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 cursor-pointer shadow-tiny"
          >
            Ver Solicitudes
          </button>
        </div>
      )}

      {/* HEADER CONTROLS AND TABS */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => setFilterTab('all')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterTab === 'all' 
                ? 'bg-slate-900 text-white shadow-xs' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Todas ({meetings.length})
          </button>

          <button
            onClick={() => setFilterTab('requested')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap ${
              filterTab === 'requested' 
                ? 'bg-amber-500 text-white shadow-xs' 
                : 'bg-amber-50 text-amber-800 border border-amber-200/60 hover:bg-amber-100'
            }`}
          >
            Solicitudes de Clientes
            {requestedCount > 0 && (
              <span className="bg-amber-700 text-white text-[10px] font-mono px-1.5 py-0.2 rounded-full font-bold">
                {requestedCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setFilterTab('scheduled')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterTab === 'scheduled' 
                ? 'bg-blue-600 text-white shadow-xs' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Programadas ({meetings.filter(m => m.status === 'Scheduled').length})
          </button>

          <button
            onClick={() => setFilterTab('completed')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              filterTab === 'completed' 
                ? 'bg-emerald-600 text-white shadow-xs' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Minutas / Completadas ({meetings.filter(m => m.status === 'Completed').length})
          </button>
        </div>

        <button
          onClick={handleOpenCreateForm}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-tiny shrink-0"
          id="btn-schedule-meet-trigger"
        >
          <Plus className="h-4 w-4" /> 
          {isClient ? 'Solicitar Reunión Virtual' : 'Agendar Nueva Junta'}
        </button>
      </div>

      {/* MAIN GRID WORKSPACE */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* MEET ROOMS LIST PANEL */}
        <div className="space-y-3" id="meetings-list-panel">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider font-mono">
              Salas Virtuales ({filteredMeetings.length})
            </h3>
          </div>

          <div className="space-y-2.5 max-h-[620px] overflow-y-auto pr-1">
            {filteredMeetings.length === 0 ? (
              <div className="p-8 text-center bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400 space-y-2">
                <Video className="h-8 w-8 mx-auto opacity-40 text-slate-400" />
                <p className="text-xs font-semibold">No se encontraron reuniones en esta categoría.</p>
              </div>
            ) : (
              filteredMeetings.map((meeting) => {
                const isSelected = selectedMeeting?.id === meeting.id;
                return (
                  <div
                    key={meeting.id}
                    onClick={() => { setSelectedMeeting(meeting); setIsInMeeting(false); setIsScheduling(false); }}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer relative group ${
                      isSelected 
                        ? 'bg-blue-50/70 border-blue-300 shadow-tiny' 
                        : 'bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-xs'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1.5 flex-1 pr-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] px-2 py-0.5 rounded-full border font-bold uppercase font-mono ${
                            meeting.status === 'Completed' 
                              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
                              : meeting.status === 'Requested'
                              ? 'bg-amber-50 border-amber-200 text-amber-700 animate-pulse'
                              : 'bg-blue-50 border-blue-200 text-blue-700'
                          }`}>
                            {meeting.status === 'Completed' 
                              ? 'Completada / Minuta Listo' 
                              : meeting.status === 'Requested'
                              ? 'Solicitud Pendiente'
                              : 'Programada'}
                          </span>

                          {meeting.meetingType && (
                            <span className="text-[9px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                              {meeting.meetingType}
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-bold text-slate-900 leading-snug">{meeting.title}</h4>
                        <p className="text-xs text-slate-600 font-medium flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 text-slate-400" />
                          {meeting.clientName}
                        </p>
                        
                        <div className="flex items-center gap-3 text-[11px] text-slate-400 font-mono font-medium pt-1">
                          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {meeting.date}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {meeting.time} hrs</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className={`p-2.5 rounded-xl ${
                          isSelected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Video className="h-4 w-4" />
                        </div>

                        {/* EDIT / DELETE QUICK ACTION BUTTONS */}
                        <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            title="Editar Reunión"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditForm(meeting);
                            }}
                            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Eliminar Reunión"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingMeetingId(meeting.id);
                            }}
                            className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* MEET CONFERENCING OR CONFIG WORKSPACE */}
        <div className="lg:col-span-2 space-y-6" id="meet-workspace-panel">
          
          {/* CASE 1: SCHEDULE / EDIT FORM */}
          {isScheduling ? (
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-100">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900">
                      {editingMeeting 
                        ? 'Editar Parámetros de la Reunión Virtual' 
                        : isClient 
                        ? 'Solicitar Reunión Virtual con Soporte Operativo' 
                        : 'Agendar Nueva Junta con Cliente'}
                    </h4>
                    <p className="text-xs text-slate-500">
                      {isClient 
                        ? 'Envía una solicitud de junta de punto a punto a nuestros operadores.' 
                        : 'Generará una sala de Google Meet con enlaces de comunicación e integraciones de IA.'}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => setIsScheduling(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Título o Motivo de la Reunión *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Revisión de Control de Calidad Lote SLP-01 / Aclaración de Prefactura..."
                    value={mTitle}
                    onChange={(e) => setMTitle(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Cliente Asociado *</label>
                    <select
                      required
                      disabled={isClient}
                      value={mClientId}
                      onChange={(e) => setMClientId(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500 disabled:opacity-75"
                    >
                      <option value="">Seleccione el cliente...</option>
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Tipo de Sesión</label>
                    <select
                      value={mType}
                      onChange={(e) => setMType(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                    >
                      <option value="Control de Calidad">Control de Calidad</option>
                      <option value="Revisión de Folio">Revisión de Folio / Proceso</option>
                      <option value="Urgencia Logística">Urgencia Logística</option>
                      <option value="Aclaración de Prefactura">Aclaración de Prefactura</option>
                      <option value="Firma de Acuerdos">Firma de Acuerdos / Minuta</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Fecha *</label>
                    <input
                      type="date"
                      required
                      value={mDate}
                      onChange={(e) => setMDate(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Hora (24 hrs) *</label>
                    <input
                      type="time"
                      required
                      value={mTime}
                      onChange={(e) => setMTime(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700">Estatus de la Junta</label>
                    <select
                      value={mStatus}
                      onChange={(e) => setMStatus(e.target.value as any)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                    >
                      <option value="Scheduled">Programada (Confirmada)</option>
                      <option value="Requested">Solicitud Pendiente</option>
                      <option value="Completed">Completada / Finalizada</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1 bg-blue-50/60 p-3 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                      <Video className="h-3.5 w-3.5 text-blue-600" />
                      Código de Sala Google Meet (Formato 3-4-3: xxx-yyyy-zzz) *
                    </label>
                    <button
                      type="button"
                      onClick={() => setMMeetCode(generateValidMeetCode())}
                      className="text-[11px] text-blue-700 hover:text-blue-900 font-bold flex items-center gap-1 cursor-pointer bg-white px-2 py-0.5 rounded border border-blue-200"
                    >
                      <RefreshCw className="h-3 w-3" /> Generar Código Válido
                    </button>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-xs text-slate-500 font-mono font-medium">https://meet.google.com/</span>
                    <input
                      type="text"
                      required
                      placeholder="abc-defg-hij"
                      value={mMeetCode}
                      onChange={(e) => setMMeetCode(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg pl-44 pr-3.5 py-2 text-xs text-slate-900 font-mono font-bold focus:outline-hidden focus:border-blue-500 shadow-tiny"
                    />
                  </div>
                  <p className="text-[10.5px] text-slate-600">
                    * Google Meet exige estrictamente el formato de 10 letras <code className="font-mono font-bold text-blue-800">xxx-yyyy-zzz</code> para evitar pantallas de comprobación de código.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700">Objetivo o Notas Adicionales</label>
                  <textarea
                    rows={3}
                    placeholder="Escriba los puntos a tratar en la sesión virtual..."
                    value={mNotes}
                    onChange={(e) => setMNotes(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-800 font-medium focus:outline-hidden focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsScheduling(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer"
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-all cursor-pointer shadow-tiny"
                  >
                    {editingMeeting ? 'Guardar Cambios' : isClient ? 'Enviar Solicitud' : 'Crear y Confirmar Sala'}
                  </button>
                </div>
              </form>
            </div>
          ) : selectedMeeting ? (
            
            <div className="space-y-6">
              
              {/* SELECTED MEETING HEADER INFO CARD */}
              <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400 uppercase font-mono font-bold tracking-wider">
                      Sala de Meet Virtual Punto a Punto
                    </span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      selectedMeeting.status === 'Completed'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : selectedMeeting.status === 'Requested'
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      {selectedMeeting.status === 'Completed' ? 'Minuta Generada' : selectedMeeting.status === 'Requested' ? 'Solicitud por Aprobada' : 'Programada'}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 leading-snug">{selectedMeeting.title}</h3>
                  <p className="text-xs text-slate-600 font-medium">
                    Cliente / Sede: <strong className="text-slate-800">{selectedMeeting.clientName}</strong>
                  </p>
                  
                  {selectedMeeting.notes && (
                    <p className="text-xs text-slate-600 italic bg-slate-50 p-2.5 rounded-xl border border-slate-200/70">
                      "{selectedMeeting.notes}"
                    </p>
                  )}

                  {/* DIRECT GOOGLE MEET LINK PANEL */}
                  <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-3 space-y-2.5 mt-2">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
                          <Video className="h-3.5 w-3.5 text-blue-600" /> Enlace Oficial de Google Meet (Verificado)
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-xs font-mono font-bold text-blue-700 bg-blue-100/70 border border-blue-200 px-2.5 py-1 rounded-md">
                            {ensureValidMeetLink(selectedMeeting.meetLink)}
                          </code>
                          <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-bold">
                            ✓ Código 3-4-3 Activo
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap shrink-0">
                        <button
                          type="button"
                          onClick={() => handleCopyMeetLink(selectedMeeting.meetLink)}
                          className="bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-tiny"
                        >
                          {copiedLink ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-slate-500" />}
                          <span>{copiedLink ? '¡Enlace Copiado!' : 'Copiar Enlace'}</span>
                        </button>

                        <a
                          href={ensureValidMeetLink(selectedMeeting.meetLink)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-tiny"
                          id="btn-open-google-meet-direct"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Abrir en Google Meet
                        </a>

                        <a
                          href="https://meet.google.com/new"
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Crear una sala nueva al instante en Google Meet"
                          className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-tiny"
                        >
                          <Plus className="h-3.5 w-3.5" /> Crear Nueva en Google
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {selectedMeeting.status === 'Requested' && !isClient && (
                    <button
                      onClick={() => handleApproveRequestedMeeting(selectedMeeting)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-tiny"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Aprobar Junta
                    </button>
                  )}

                  {selectedMeeting.status !== 'Completed' && !isInMeeting && (
                    <button
                      onClick={handleEnterRoom}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-xs"
                      id="btn-enter-virtual-meet"
                    >
                      <MonitorPlay className="h-4 w-4" /> Entrar a Sala Virtual
                    </button>
                  )}

                  <button
                    onClick={() => handleOpenEditForm(selectedMeeting)}
                    title="Editar Reunión"
                    className="p-2 text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setDeletingMeetingId(selectedMeeting.id)}
                    title="Eliminar Reunión"
                    className="p-2 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-colors cursor-pointer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* CONFERENCING WORKSPACE (WHEN ACTIVE IN MEETING) */}
              {isInMeeting ? (
                <div className="bg-slate-950 text-white p-6 rounded-2xl border border-slate-800 space-y-6 shadow-xl relative overflow-hidden">
                  
                  {/* CALL TOP BAR */}
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-3 w-3 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                      </span>
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-300 font-mono flex items-center gap-2">
                          Sesión Meet en Vivo • <span className="text-emerald-400">{formatTimer(callTimer)}</span>
                        </h4>
                        <p className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3 text-emerald-400" />
                          Conexión Punto a Punto Encriptada WebRTC • Latencia 18ms
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowChatDrawer(!showChatDrawer)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                          showChatDrawer ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <MessageSquare className="h-4 w-4" /> Chat ({chatMessages.length})
                      </button>

                      <button 
                        onClick={handleLeaveRoom}
                        className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-xl transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <PhoneOff className="h-4 w-4" /> Salir de Sala
                      </button>
                    </div>
                  </div>

                  {/* SCREEN SHARE CONTAINER (IF ACTIVE) */}
                  {isScreenSharing && (
                    <div className="bg-black rounded-xl border border-blue-500/50 p-2 relative space-y-2">
                      <div className="flex items-center justify-between px-2 text-xs font-mono text-blue-400 font-bold">
                        <span className="flex items-center gap-1.5">
                          <Monitor className="h-4 w-4 animate-pulse" /> Presentando Pantalla del Sistema en Tiempo Real
                        </span>
                        <button
                          onClick={toggleScreenShare}
                          className="text-slate-400 hover:text-white text-[10px] underline"
                        >
                          Detener Pantalla
                        </button>
                      </div>
                      <video
                        ref={screenShareVideoRef}
                        autoPlay
                        playsInline
                        className="w-full h-64 bg-slate-900 rounded-lg object-contain"
                      />
                    </div>
                  )}

                  {/* MAIN VIDEO CALL GRID & CHAT PANEL */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* VIDEO CONTAINER 1: HOST / INTERNAL USER */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 relative min-h-[220px] flex flex-col items-center justify-center overflow-hidden">
                      {isVideoOn ? (
                        <video
                          ref={localVideoRef}
                          autoPlay
                          playsInline
                          muted
                          className="absolute inset-0 w-full h-full object-cover rounded-2xl"
                        />
                      ) : null}

                      {(!isVideoOn || !mediaStreamRef.current) && (
                        <div className="flex flex-col items-center justify-center space-y-2 z-10">
                          <div className="h-20 w-20 bg-blue-600/90 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg border-2 border-blue-400">
                            {currentUser.name.substring(0, 2).toUpperCase()}
                          </div>
                          <span className="text-xs font-bold text-slate-200">{currentUser.name} (Tú)</span>
                        </div>
                      )}

                      <div className="absolute bottom-3 left-3 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-lg border border-slate-800 text-[10px] font-mono text-slate-300 flex items-center gap-1.5 z-20">
                        {isMuted ? <MicOff className="h-3 w-3 text-red-400" /> : <Mic className="h-3 w-3 text-emerald-400" />}
                        <span>Anfitrión Operativo</span>
                      </div>
                    </div>

                    {/* VIDEO CONTAINER 2: CLIENT PARTICIPANT */}
                    <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 relative min-h-[220px] flex flex-col items-center justify-center overflow-hidden">
                      <div className="flex flex-col items-center justify-center space-y-2 z-10">
                        <div className="h-20 w-20 bg-emerald-600/90 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg border-2 border-emerald-400 animate-pulse">
                          {selectedMeeting.clientName.substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-xs font-bold text-slate-200">{selectedMeeting.clientName}</span>
                        <span className="text-[10px] text-emerald-400 font-mono bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-800">
                          ● Cliente Conectado
                        </span>
                      </div>

                      <div className="absolute bottom-3 left-3 bg-slate-950/80 backdrop-blur-md px-2.5 py-1 rounded-lg border border-slate-800 text-[10px] font-mono text-slate-300 flex items-center gap-1.5 z-20">
                        <Mic className="h-3 w-3 text-emerald-400" />
                        <span>Socio Comercial</span>
                      </div>
                    </div>
                  </div>

                  {/* IN-CALL CONTROL BAR */}
                  <div className="flex items-center justify-center gap-3 bg-slate-900/80 p-3 rounded-2xl border border-slate-800">
                    <button
                      onClick={toggleMute}
                      className={`p-3 rounded-xl transition-all cursor-pointer ${
                        isMuted 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      }`}
                      title={isMuted ? 'Desactivar Silencio' : 'Silenciar Micrófono'}
                    >
                      {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </button>

                    <button
                      onClick={toggleCamera}
                      className={`p-3 rounded-xl transition-all cursor-pointer ${
                        !isVideoOn 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      }`}
                      title={isVideoOn ? 'Apagar Cámara' : 'Encender Cámara'}
                    >
                      {!isVideoOn ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
                    </button>

                    <button
                      onClick={toggleScreenShare}
                      className={`p-3 rounded-xl transition-all cursor-pointer ${
                        isScreenSharing 
                          ? 'bg-blue-600 text-white shadow-xs' 
                          : 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      }`}
                      title="Compartir Pantalla"
                    >
                      <Monitor className="h-5 w-5" />
                    </button>

                    <button
                      onClick={handleToggleRecording}
                      className={`px-4 py-3 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                        isRecording 
                          ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse' 
                          : 'bg-slate-800 text-amber-400 hover:bg-slate-700 border border-slate-700'
                      }`}
                    >
                      {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                      <span>{isRecording ? 'Detener Grabación' : 'Grabar Transcripción AI'}</span>
                    </button>
                  </div>

                  {/* IN-CALL LIVE CHAT DRAWER */}
                  {showChatDrawer && (
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 space-y-3 animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                        <h5 className="text-xs font-bold uppercase tracking-wider text-slate-300 font-mono flex items-center gap-1.5">
                          <MessageSquare className="h-4 w-4 text-blue-400" /> Chat de la Conferencia Punto a Punto
                        </h5>
                        <button onClick={() => setShowChatDrawer(false)} className="text-slate-400 hover:text-white">
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1 text-xs">
                        {chatMessages.map(msg => (
                          <div
                            key={msg.id}
                            className={`p-2.5 rounded-xl space-y-1 ${
                              msg.isClient 
                                ? 'bg-slate-800/80 border border-slate-700 text-slate-200' 
                                : 'bg-blue-950/60 border border-blue-900 text-blue-100 ml-4'
                            }`}
                          >
                            <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                              <span className="font-bold text-blue-400">{msg.sender}</span>
                              <span>{msg.timestamp}</span>
                            </div>
                            <p>{msg.text}</p>
                          </div>
                        ))}
                      </div>

                      <form onSubmit={handleSendChatMessage} className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Escriba un mensaje para la sesión..."
                          value={newChatMessage}
                          onChange={(e) => setNewChatMessage(e.target.value)}
                          className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-hidden focus:border-blue-500"
                        />
                        <button
                          type="submit"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer"
                        >
                          <Send className="h-3.5 w-3.5" />
                        </button>
                      </form>
                    </div>
                  )}

                  {/* AI TRANSCRIPTION AND MINUTA SECTION */}
                  <div className="space-y-4 border-t border-slate-800 pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="space-y-0.5">
                        <h5 className="text-xs font-bold text-yellow-400 flex items-center gap-1.5 font-mono uppercase">
                          <Sparkles className="h-4 w-4" /> Transcriptor por Voz e Inteligencia Artificial (Gemini)
                        </h5>
                        <p className="text-[10px] text-slate-400">
                          Escucha los acuerdos durante la llamada y genera la minuta técnica en tiempo real.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setManualTranscriptInput(
                          `El cliente ${selectedMeeting.clientName} solicita acelerar la inspección técnica de control de calidad para las piezas enviadas. Acuerdan priorizar la liberación y generar de inmediato la prefactura correspondiente tras la entrega en planta.`
                        )}
                        className="text-[10px] text-yellow-400 hover:underline font-mono"
                      >
                        Cargar Minuta Demo de Muestra
                      </button>
                    </div>

                    {isRecording && (
                      <div className="space-y-2 bg-slate-900/90 p-3 rounded-xl border border-amber-500/30">
                        <canvas ref={canvasRef} width={400} height={35} className="w-full bg-slate-950 rounded-lg border border-slate-800" />
                        <p className="text-[10px] text-center text-yellow-400 italic font-medium animate-pulse">
                          Escuchando micrófono y registrando minuta de conferencia...
                        </p>
                      </div>
                    )}

                    <div className="space-y-1">
                      <textarea
                        value={manualTranscriptInput}
                        onChange={(e) => setManualTranscriptInput(e.target.value)}
                        placeholder="La transcripción capturada por voz aparecerá aquí o redacte notas adicionales..."
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3.5 py-2.5 text-xs text-white focus:outline-hidden focus:border-yellow-400 h-24 font-mono"
                      />
                    </div>

                    <button
                      onClick={handleGenerateAIReport}
                      disabled={isProcessingAI || !manualTranscriptInput}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-slate-950 font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2 shadow-md cursor-pointer"
                    >
                      <Sparkles className="h-4 w-4" /> 
                      {isProcessingAI ? 'Procesando Minuta y Acuerdos con Gemini AI...' : 'Generar Minuta y Finalizar Reunión'}
                    </button>
                  </div>
                </div>
              ) : selectedMeeting.status === 'Completed' ? (
                
                /* COMPLETED MINUTA EXECUTIVE DISPLAY */
                <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-6" id="completed-meet-minuta-report">
                  
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                    <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900">Reporte Ejecutivo de Junta y Acuerdos</h4>
                      <p className="text-xs text-slate-500">Minuta sintetizada mediante Inteligencia Artificial Gemini</p>
                    </div>
                  </div>

                  {/* Summary Section */}
                  <div className="space-y-2 bg-slate-50/70 p-4 rounded-xl border border-slate-200/60">
                    <h5 className="text-xs font-bold text-slate-900 flex items-center gap-1.5 text-emerald-800 uppercase tracking-wider font-mono">
                      <BookOpen className="h-4 w-4 text-emerald-600" /> Resumen Ejecutivo de la Junta
                    </h5>
                    <p className="text-xs text-slate-700 leading-relaxed font-medium">
                      {selectedMeeting.summary || 'Sin resumen registrado.'}
                    </p>
                  </div>

                  {/* Formal Agreements List */}
                  <div className="space-y-2.5">
                    <h5 className="text-xs font-bold text-slate-900 flex items-center gap-1.5 text-blue-800 uppercase tracking-wider font-mono">
                      <CheckSquare className="h-4 w-4 text-blue-600" /> Acuerdos Formales Pactados
                    </h5>
                    
                    {selectedMeeting.agreements && selectedMeeting.agreements.length > 0 ? (
                      <ul className="space-y-2">
                        {selectedMeeting.agreements.map((agr, index) => (
                          <li key={index} className="text-xs text-slate-700 flex items-start gap-2.5 bg-blue-50/40 p-3 rounded-xl border border-blue-100/60 font-medium">
                            <span className="font-mono text-blue-600 font-bold shrink-0">{index + 1}.</span>
                            <span>{agr}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No se registraron acuerdos específicos.</p>
                    )}
                  </div>

                  {/* Transcription Raw Drawer */}
                  {selectedMeeting.transcription && (
                    <div className="space-y-1 border-t border-slate-100 pt-4">
                      <label className="text-[10px] text-slate-400 uppercase font-bold font-mono">Transcripción Completa del Audio</label>
                      <p className="text-xs text-slate-600 italic bg-slate-50 p-3 rounded-xl border border-slate-200/60 select-all max-h-28 overflow-y-auto font-mono">
                        "{selectedMeeting.transcription}"
                      </p>
                    </div>
                  )}

                </div>
              ) : (
                /* SCHEDULED MEETING NOT YET ACTIVE */
                <div className="bg-white p-8 rounded-2xl border border-slate-200/80 text-center py-16 text-slate-400 space-y-3 shadow-xs">
                  <div className="h-12 w-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto border border-blue-100">
                    <Video className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-800">Esta sesión está programada</h4>
                    <p className="text-xs text-slate-500 max-w-md mx-auto mt-1">
                      Haga clic en <strong>"Entrar a Sala Virtual"</strong> para iniciar la transmisión en vivo de punto a punto, activar cámara/micrófono y transcribir acuerdos con IA.
                    </p>
                  </div>
                  <button
                    onClick={handleEnterRoom}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-xs font-bold transition-all inline-flex items-center gap-2 cursor-pointer shadow-tiny mt-2"
                  >
                    <MonitorPlay className="h-4 w-4" /> Entrar a Sala Virtual Ahora
                  </button>
                </div>
              )}

            </div>
          ) : (
            <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-slate-400">
              Seleccione o agende una sala de reunión virtual.
            </div>
          )}

        </div>

      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {deletingMeetingId && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-slate-200 shadow-xl space-y-4 animate-scaleUp">
            <div className="flex items-center gap-3 text-red-600">
              <div className="p-2.5 bg-red-50 rounded-xl border border-red-100">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h4 className="text-base font-bold text-slate-900">¿Eliminar Reunión Virtual?</h4>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              Esta acción eliminará la sala de junta y removerá los eventos vinculados en el calendario de logística. ¿Desea continuar?
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDeletingMeetingId(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleConfirmDelete(deletingMeetingId)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-xs transition-all cursor-pointer shadow-tiny"
              >
                Sí, Eliminar Reunión
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
